import type * as React from 'react'

/**
 * Delay before expanding the comment after scroll starts, so the smooth scroll
 * animation is visible before the comment expands.
 */
const EXPAND_AFTER_SCROLL_DELAY_MS = 400

/**
 * Scrolls to an element and expands it after a short delay. If the element
 * isn't found (e.g. in tests with no rendered DOM), expands immediately.
 * Returns a cleanup function to cancel the pending expansion. Respects the
 * user's `prefers-reduced-motion` setting.
 *
 * This is comment-specific presentation polish layered over the shared
 * coordination store's expand-next: mentions advance immediately and let the
 * newly expanded card's textarea focus bring it into view.
 */
export const scrollToAndExpand = (
  elementSelector: string,
  onExpand: () => void,
  isMountedRef?: React.MutableRefObject<boolean>,
): (() => void) => {
  let timeoutId: NodeJS.Timeout | null = null

  const cleanup = (): void => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  const element = document.querySelector(elementSelector)
  if (element) {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    element.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    })
    timeoutId = setTimeout(() => {
      if (!isMountedRef || isMountedRef.current) {
        onExpand()
      }
    }, EXPAND_AFTER_SCROLL_DELAY_MS)
  } else if (!isMountedRef || isMountedRef.current) {
    onExpand()
  }

  return cleanup
}
